import React, { useState } from 'react';
import { UserPlus, Edit, Calendar, Search, Filter, Star, Mail, Phone, Stethoscope, User, CalendarOff } from 'lucide-react';
import { getStaffFullName } from '../../../../utils/staffUtils';
import { staffService } from '../../../../services/staffService';

const StaffList = ({
  staff,
  loading,
  onCreateStaff,
  onEditStaff,
  onManageAvailability,
  onManageLeave,
  filterRole,
  onFilterChange,
  onStaffUpdated
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});

  const handleToggleStatus = async (staffMember) => {
    const newStatus = !staffMember.isActive;
    setUpdatingStatus(prev => ({ ...prev, [staffMember.id]: true }));

    try {
      await staffService.updateStaffStatus(staffMember.id, newStatus);
      if (onStaffUpdated) {
        onStaffUpdated();
      }
    } catch (error) {
      console.error('Error updating staff status:', error);
      alert('Failed to update staff status. Please try again.');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [staffMember.id]: false }));
    }
  };

  const filteredStaff = staff.filter(s => {
    // Filter by search term
    const matchesSearch = s.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.specializations?.some(spec => spec.toLowerCase().includes(searchTerm.toLowerCase()));

    // Filter by active/inactive status
    // showInactive = false → Show only active staff (isActive !== false)
    // showInactive = true → Show all staff (both active and inactive)
    const matchesActiveStatus = showInactive ? true : (s.isActive !== false);

    return matchesSearch && matchesActiveStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, email, or specialization..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={filterRole}
              onChange={(e) => onFilterChange(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">All Roles</option>
              <option value="doctor">Doctors</option>
              <option value="therapist">Therapists</option>
            </select>
          </div>

          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center gap-2 ${!showInactive
              ? 'bg-gray-600 text-white border-gray-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            title={!showInactive ? 'Showing active staff only' : 'Showing all staff'}
          >
            <CalendarOff className="w-5 h-5" />
            {!showInactive ? 'Show Active Only' : 'Show All'}
          </button>

          <button
            onClick={onCreateStaff}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <UserPlus className="w-5 h-5" />
            Add Staff
          </button>
        </div>
      </div>

      {/* Staff Grid */}
      {filteredStaff.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No staff members found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStaff.map((staffMember) => (
            <div
              key={staffMember.id}
              className={`bg-white border rounded-lg p-6 hover:shadow-lg transition-shadow ${staffMember.isActive === false
                ? 'border-gray-300 bg-gray-50 opacity-75'
                : 'border-gray-200'
                }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${staffMember.isActive === false
                    ? 'bg-gray-200'
                    : staffMember.role === 'doctor'
                      ? 'bg-purple-100'
                      : 'bg-green-100'
                    }`}>
                    {staffMember.avatarUrl ? (
                      <img
                        src={staffMember.avatarUrl}
                        alt={staffMember.fullName}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : staffMember.role === 'doctor' ? (
                      <Stethoscope className={`w-6 h-6 ${staffMember.isActive === false ? 'text-gray-500' : 'text-purple-600'
                        }`} />
                    ) : (
                      <User className={`w-6 h-6 ${staffMember.isActive === false ? 'text-gray-500' : 'text-green-600'
                        }`} />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">
                      {getStaffFullName(staffMember.fullName, staffMember.role, staffMember.gender)}
                    </h3>
                    <div className="flex gap-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${staffMember.role === 'doctor'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-green-100 text-green-700'
                        }`}>
                        {staffMember.role.charAt(0).toUpperCase() + staffMember.role.slice(1)}
                      </span>
                      {staffMember.isActive === false && (
                        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                {staffMember.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{staffMember.email}</span>
                  </div>
                )}
                {staffMember.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>{staffMember.phone}</span>
                  </div>
                )}
              </div>

              {/* Specializations */}
              {staffMember.specializations && staffMember.specializations.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {staffMember.specializations.slice(0, 2).map((spec, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {spec}
                      </span>
                    ))}
                    {staffMember.specializations.length > 2 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                        +{staffMember.specializations.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-sm font-semibold">{staffMember.rating?.toFixed(1) || 'N/A'}</span>
                </div>
                {staffMember.experienceYears && (
                  <span className="text-sm text-gray-600">{staffMember.experienceYears} years exp.</span>
                )}
              </div>

              {/* Active/Inactive Toggle */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b">
                <span className="text-sm font-medium text-gray-700">Status</span>
                <button
                  onClick={() => handleToggleStatus(staffMember)}
                  disabled={updatingStatus[staffMember.id]}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${updatingStatus[staffMember.id]
                    ? 'opacity-50 cursor-not-allowed'
                    : staffMember.isActive
                      ? 'bg-green-500'
                      : 'bg-gray-300'
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${staffMember.isActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => onEditStaff(staffMember)}
                    className="flex-1 px-3 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => onManageAvailability(staffMember)}
                    className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <Calendar className="w-4 h-4" />
                    Schedule
                  </button>
                </div>
                <button
                  onClick={() => onManageLeave(staffMember)}
                  className="w-full px-3 py-2 border border-orange-600 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <CalendarOff className="w-4 h-4" />
                  Manage Leave
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaffList;

